import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { TResponse, TResponseWithQData } from "../../../types/common";
import axios from "../../../lib/axios";

// Must match the `tenders` table exactly (see
// api/src/db/migrations/20260608200003_create_documents_s21.ts) and what
// the backend controller actually reads/writes
// (api/src/packages/company/v1/modules/tenders/controller.ts). A second,
// later migration (20260612400002_create_tenders.ts) defined a different
// shape (tender_number/tender_value/decision_date/issuing_authority/
// contract_type/description, and a "preparing"|"under_review"|"no_bid"
// status enum) but never actually took effect — it's a no-op because the
// table already existed from the first migration by the time it ran. This
// type previously matched that dead migration instead of the live schema,
// so tender_value/decision_date were silently dropped on every save and
// picking "preparing"/"under_review"/"no_bid" crashed the insert with a raw
// "Data truncated for column 'status'" MySQL error.
export type TenderStatus = "tracking" | "submitted" | "won" | "lost" | "withdrawn";

export interface ITender {
  id: number;
  company_id: number;
  client_id: number | null;
  client_name: string | null;
  reference: string | null;
  title: string;
  status: TenderStatus;
  value_estimate: number | null;
  submission_deadline: string | null;
  submitted_at: string | null;
  outcome_date: string | null;
  document_url: string | null;
  notes: string | null;
  created_at: string;
}

const listTenders = (params: any) =>
  axios.get(API_ENDPOINTS.TENDER_LIST, { params }).then((r) => r.data as TResponseWithQData<ITender[]>);

const createTender = (data: any) =>
  axios.post(API_ENDPOINTS.TENDER_CREATE, data).then((r) => r.data as TResponse<ITender>);

const updateTender = (data: any) =>
  axios.post(API_ENDPOINTS.TENDER_UPDATE, data).then((r) => r.data as TResponse<ITender>);

const deleteTender = (id: number) =>
  axios.post(API_ENDPOINTS.TENDER_DELETE, { id }).then((r) => r.data as TResponse<null>);

export const useTenderList = (initialParams: any) => {
  const [params, setParams] = useState(initialParams);
  const result = useQuery<TResponseWithQData<ITender[]>, Error>({
    queryKey: [API_ENDPOINTS.TENDER_LIST, params],
    queryFn: () => listTenders(params),
  });
  return { ...result, refetchWithQuery: (p: any) => setParams((prev: any) => ({ ...prev, ...p })) };
};

export const useTenderCreate = (options?: { onSuccess?: (d: ITender) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation<TResponse<ITender>, Error, any>({
    mutationFn: createTender,
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.TENDER_LIST] }); options?.onSuccess?.(res.data as ITender); },
    onError: options?.onError,
  });
};

export const useTenderUpdate = (options?: { onSuccess?: (d: ITender) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation<TResponse<ITender>, Error, any>({
    mutationFn: updateTender,
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.TENDER_LIST] }); options?.onSuccess?.(res.data as ITender); },
    onError: options?.onError,
  });
};

export const useTenderDelete = (options?: { onSuccess?: () => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation<TResponse<null>, Error, number>({
    mutationFn: deleteTender,
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.TENDER_LIST] }); options?.onSuccess?.(); },
    onError: options?.onError,
  });
};
